import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Building2 } from 'lucide-react';

const AreaSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="area" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-stone-50 to-stone-100">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-800 mb-4">
            対応エリアと会社のご案内
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Left Column: Niigata Map + Images */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8 h-full flex flex-col"
          >
            {/* Niigata Map Container */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-stone-200 flex-grow flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-8 h-8 text-amber-600" />
                <h3 className="text-2xl font-bold text-stone-800">
                  池田石材興業 新潟店
                </h3>
              </div>

              <div className="rounded-xl overflow-hidden shadow-lg border border-stone-200 mb-6">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6361.615887108158!2d138.74759699999998!3d37.133483999999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5ff5ee98036702a7%3A0x6d1b9291ef9004b8!2z77yI5qCq77yJ5rGg55Sw55-z5p2Q6IiI5qWtIOacrOekvg!5e0!3m2!1sja!2sjp!4v1763742550205!5m2!1sja!2sjp"
                  title="池田石材興業 新潟店"
                  loading="lazy"
                  className="w-full h-64 md:h-80"
                  style={{ border: 0 }}
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <a
                href="https://maps.app.goo.gl/gfHtvFhsDukLmmu39?g_st=ipc"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full text-center px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-full transition-all duration-300 shadow-md mb-8"
              >
                新潟店をGoogleマップで開く
              </a>

              {/* Images inside Niigata container */}
              <div className="mt-auto">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="images/88.png" 
                    alt="池田石材興業の様子1" 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Nagano Map + Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-8 h-full flex flex-col"
          >
            {/* Nagano Map Container */}
            <div className="bg-white rounded-xl p-8 shadow-lg border border-stone-200 flex-grow flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-8 h-8 text-amber-600" />
                <h3 className="text-2xl font-bold text-stone-800">
                  池田石材興業 長野店
                </h3>
              </div>

              <div className="rounded-xl overflow-hidden shadow-lg border border-stone-200 mb-6">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3197.9250903256243!2d138.30199607637718!3d36.724360271970326!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x601d899133b21abf%3A0x76c7f88fae73bd50!2z5qCq5byP5Lya56S-5rGg55Sw55-z5p2Q6IiI5qWtIOmVt-mHjuaUr-ekvg!5e0!3m2!1sja!2sjp!4v1764337075362!5m2!1sja!2sjp"
                  title="(株)池田石材興業 長野店"
                  loading="lazy"
                  className="w-full h-64 md:h-80"
                  style={{ border: 0 }}
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <a
                href="https://maps.app.goo.gl/9eieKsvoEm2HCHVm6"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full text-center px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-full transition-all duration-300 shadow-md mb-8"
              >
                長野店をGoogleマップで開く
              </a>

              {/* Image under Nagano Map */}
              <div className="mt-auto">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="images/87.png" 
                    alt="池田石材興業の様子3" 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Company Info Section - Separate Frame */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-xl p-8 shadow-lg border border-stone-200 max-w-4xl mx-auto"
        >
          <div className="flex items-center gap-3 mb-8 justify-center">
            <Building2 className="w-8 h-8 text-amber-600" />
            <h3 className="text-2xl font-bold text-stone-800">会社情報</h3>
          </div>

          <div className="space-y-10 text-stone-700">
            {/* Intro */}
            <div className="text-center">
              <h4 className="text-lg font-bold text-stone-800 mb-2">【創業80年の信頼と実績】</h4>
              <p className="leading-relaxed">
                私たちは長年培った確かな技術で、お墓の建立から供養のお手伝いまで、<br className="hidden sm:block" />
                石材に関するあらゆるニーズにお応えします。
              </p>
            </div>

            {/* Business Content */}
            <div>
              <h4 className="text-lg font-bold text-stone-800 border-l-4 border-amber-500 pl-3 mb-4">
                事業内容
              </h4>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><span className="font-bold">お墓に関すること</span>：墓石の建立、墓石解体、墓じまい</li>
                <li><span className="font-bold">供養のサポート</span>：改葬サポート、手元供養のご提案、デジタルメモリアル制作</li>
                <li><span className="font-bold">造形・制作</span>：モニュメント制作、オリジナル石材加工</li>
              </ul>
            </div>

            {/* Area */}
            <div>
              <h4 className="text-lg font-bold text-stone-800 border-l-4 border-amber-500 pl-3 mb-4">
                対応エリア
              </h4>
              <p className="ml-2">
                新潟県・長野県（各店舗の商圏および、拠点より100km圏内）
              </p>
            </div>

            {/* Qualifications */}
            <div>
              <h4 className="text-lg font-bold text-stone-800 border-l-4 border-amber-500 pl-3 mb-4">
                保有資格・許認可
              </h4>
              <p className="mb-4 ml-2">
                技術・法令遵守の両面で、国や自治体から認められた高い専門性を備えています。
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="bg-stone-100 text-stone-700">
                    <tr>
                      <th className="p-3 border border-stone-200 whitespace-nowrap">区分</th>
                      <th className="p-3 border border-stone-200 whitespace-nowrap">資格・認定名称</th>
                      <th className="p-3 border border-stone-200 whitespace-nowrap">発行元</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 border border-stone-200">建設業許可</td>
                      <td className="p-3 border border-stone-200">新潟県知事許可</td>
                      <td className="p-3 border border-stone-200">新潟県</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-stone-200">国家資格</td>
                      <td className="p-3 border border-stone-200">一級石材施工技能士</td>
                      <td className="p-3 border border-stone-200">厚生労働省</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-stone-200">技術評価</td>
                      <td className="p-3 border border-stone-200">建設技能者能力評価（レベル3）</td>
                      <td className="p-3 border border-stone-200">国土交通省</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-stone-200">技術評価</td>
                      <td className="p-3 border border-stone-200">石匠位</td>
                      <td className="p-3 border border-stone-200">経済産業省</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-stone-200">技術評価</td>
                      <td className="p-3 border border-stone-200">日本墓石店100選認定店</td>
                      <td className="p-3 border border-stone-200">日本墓石店100選</td>
                    </tr>
                    <tr>
                      <td className="p-3 border border-stone-200">指導資格</td>
                      <td className="p-3 border border-stone-200">職業訓練指導員免許</td>
                      <td className="p-3 border border-stone-200">新潟県</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Achievements */}
            <div>
              <h4 className="text-lg font-bold text-stone-800 border-l-4 border-amber-500 pl-3 mb-4">
                実績
              </h4>
              <ul className="space-y-3 ml-2">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">●</span>
                  <span>弊社施工の記念碑が自然災害伝承碑として、国土地理院に掲載</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">●</span>
                  <span>アントニーゴームリー（イギリス）　MAN ROCK V　大地の芸術祭にてコラボ</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">●</span>
                  <span>ニキータ・カダン（ウクライナ）　影・旗・衛星・道路　大地の芸術祭にてコラボ</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">●</span>
                  <span>月刊石材新聞に掲載（2回）、日本石材新聞に掲載（2回）、季刊温泉に掲載</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AreaSection;
